import { Box, LinearProgress, makeStyles } from '@material-ui/core';
import { DataGridProps, GridRowParams, MuiEvent } from '@mui/x-data-grid';
import React from 'react';

import {
  CustomGridColDef,
  FetchingDataGrid,
  FetchingDataGridProps,
  ListPayload,
} from '@/components/shared/components/FetchingDataGrid/FetchingDataGrid';
import { Text } from '@/components/shared/components/Text';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { ListQueryProps } from '@/types/ListQueryProps';

interface StandardListProps extends Omit<FetchingDataGridProps, 'columns' | 'fetch'> {
  dataGridFields: CustomGridColDef[];
  fetchData: (query: ListQueryProps) => Promise<ListPayload>;
  fetchingDataGridProps?: DataGridProps;
  onRowClick?: (params: GridRowParams, event: MuiEvent<React.SyntheticEvent<Element, Event>>, details?) => void;
  style?: React.CSSProperties;
  sortingOrder?: ('asc' | 'desc' | null | undefined)[];
}

export function StandardList({
  dataGridFields,
  fetchData,
  getXLSXExport,
  filters,
  booleanFilterTools,
  defaultOrderBy = 'created_at',
  defaultOrderDirection = 'desc',
  defaultPageSize = 10,
  showExport,
  getRowClassName,
  showSearch = true,
  liveUpdates,
  ...fetchingDataGridProps
}: StandardListProps) {
  const {
    state: { dictionaryValues },
  } = useConfigContext();

  const { palette } = useCustomTheme();

  const useStyles = makeStyles({
    root: {
      '& .last-viewed-application': {
        backgroundColor: palette.grey[50],
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        whiteSpace: 'normal',
        lineHeight: 1.3,
        flexGrow: 1,
      },
      '&.MuiDataGrid-toolbarContainer': {
        justifyContent: 'space-between',
      },
    },
  });

  const classes = useStyles();

  const rowsPerPage = [10, 25, 50, 100];
  const defaultPageSizeIndex = rowsPerPage.findIndex((n) => n === defaultPageSize);
  if (defaultPageSizeIndex !== -1) {
    rowsPerPage.splice(defaultPageSizeIndex, 1);
    rowsPerPage.unshift(defaultPageSize);
  }

  return dataGridFields ? (
    <div>
      <Box>
        <div className={`appear-anim ${classes.root}`} style={{ fontSize: '0.9rem', height: '100%', width: '100%' }}>
          {dictionaryValues && (
            <FetchingDataGrid
              sortingOrder={fetchingDataGridProps?.sortingOrder ?? ['asc', 'desc']}
              getRowClassName={getRowClassName}
              getXLSXExport={getXLSXExport}
              filters={filters}
              rowsPerPageOptions={rowsPerPage}
              defaultOrderBy={defaultOrderBy}
              defaultOrderDirection={defaultOrderDirection}
              showExport={showExport}
              showRefresh={true}
              showSearch={showSearch}
              defaultPageSize={defaultPageSize} // TODO fix, not working properly
              booleanFilterTools={booleanFilterTools}
              liveUpdates={liveUpdates}
              {...fetchingDataGridProps}
              columns={dataGridFields}
              fetch={(q: ListQueryProps) =>
                fetchData({
                  ...q,
                  ...filters,
                })
              }
            />
          )}
        </div>
      </Box>
    </div>
  ) : (
    <div style={{ overflow: 'hidden' }}>
      <Box p={5} textAlign={'center'}>
        <h5>
          <Text tId={'LOADING_DATA'} />
        </h5>
      </Box>
      <LinearProgress />
    </div>
  );
}
